/**
 * Variant Product Gallery Fallback
 * Handles image fallback for variant SKUs that don't have images
 * Falls back to base product images from Shopify
 */

(function() {
    // Function to check if product has only placeholder image
    function hasOnlyPlaceholder() {
        var viewer = document.querySelector('[data-gallery-viewer]');
        if (!viewer) return false;
        
        var placeholder = viewer.querySelector('.product-gallery--image-placeholder');
        return placeholder !== null;
    }
    
    // Function to get base SKU from variant SKU
    function getBaseSKU(variantSKU) {
        if (!variantSKU) return null;
        // Remove decimal part (e.g., "P4440.20" -> "P4440", "RB4322.10" -> "RB4322")
        return variantSKU.replace(/\.\d+$/, '');
    }
    
    // Function to detect product type from SKU prefix
    function getProductTypeFromSKU(sku) {
        if (!sku) return 'product';
        var skuLower = sku.toLowerCase();
        
        // Check 3-character prefixes first (like "fgs")
        var threeCharPrefix = skuLower.substring(0, 3);
        var threeCharMap = {
            'fgs': 'full-gasket-set',
            'ek2': 'engine-rebuild-kit'
        };
        if (threeCharMap[threeCharPrefix]) {
            return threeCharMap[threeCharPrefix];
        }
        
        // Check 2-character prefixes
        var prefix = skuLower.substring(0, 2);
        var productTypeMap = {
            'p4': 'piston-set',
            'pr': 'piston-ring-set',
            'rb': 'rod-bearings-set',
            'mb': 'main-bearings-set',
            'op': 'oil-pump',
            'tb': 'timing-belt-kit',
            'tw': 'timing-chain-kit',
            'ek': 'engine-rebuild-kit',
            'cs': 'camshaft',
            'vk': 'valve-kit',
            'wp': 'water-pump'
        };
        return productTypeMap[prefix] || 'product';
    }
    
    // Function to check if product has images
    function hasProductImages(productData) {
        return productData && productData.media && productData.media.length > 0;
    }
    
    // Function to fetch product by handle (silently handles 404s)
    function fetchProductByHandle(handle, callback, errorCallback) {
        var apiUrl = '/products/' + handle + '.js';
        
        fetch(apiUrl)
            .then(function(response) {
                if (!response.ok) {
                    // Silently handle 404s - these are expected when products don't exist
                    throw new Error('Product not found');
                }
                return response.json();
            })
            .then(function(productData) {
                callback(productData);
            })
            .catch(function(error) {
                // Silently handle errors - don't log expected 404s
                if (errorCallback) {
                    errorCallback(error);
                }
            });
    }
    
    // Function to search products by SKU using Shopify search API
    function searchProductsBySKU(sku, callback, errorCallback) {
        // Try search API first (returns HTML with product links)
        var searchUrl = '/search?type=product&q=' + encodeURIComponent(sku);
        
        fetch(searchUrl)
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Search failed');
                }
                return response.text();
            })
            .then(function(html) {
                // Parse the HTML response to extract product handles
                var parser = new DOMParser();
                var doc = parser.parseFromString(html, 'text/html');
                var handles = [];
                
                // Look for product links in various formats
                var productLinks = doc.querySelectorAll('a[href*="/products/"]');
                productLinks.forEach(function(link) {
                    var href = link.getAttribute('href');
                    if (href) {
                        var match = href.match(/\/products\/([^?#\/]+)/);
                        if (match && match[1]) {
                            var handle = match[1];
                            if (handles.indexOf(handle) === -1) {
                                handles.push(handle);
                            }
                        }
                    }
                });
                
                // Also check for data attributes that might contain handles
                var productItems = doc.querySelectorAll('[data-product-handle], [attr-prod-sku]');
                productItems.forEach(function(item) {
                    var handle = item.getAttribute('data-product-handle');
                    if (handle && handles.indexOf(handle) === -1) {
                        handles.push(handle);
                    }
                });
                
                if (handles.length > 0) {
                    callback(handles);
                } else if (errorCallback) {
                    errorCallback();
                }
            })
            .catch(function(error) {
                if (errorCallback) {
                    errorCallback(error);
                }
            });
    }
    
    // Function to fetch product by searching for SKU and then fetching by handle
    function fetchProductBySKUSearch(sku, baseSKU, callback, maxAttempts) {
        maxAttempts = maxAttempts || 4; // Default to 4 attempts max
        
        searchProductsBySKU(sku, function(handles) {
            // Limit handles to max attempts
            var handlesToTry = handles.slice(0, maxAttempts);
            var baseSKULower = baseSKU ? baseSKU.toLowerCase() : '';
            
            function tryHandle(index) {
                if (index >= handlesToTry.length || index >= maxAttempts) {
                    return; // All handles tried or max attempts reached
                }
                
                var handle = handlesToTry[index];
                fetchProductByHandle(handle, function(productData) {
                    // Check if product has images first
                    if (!hasProductImages(productData)) {
                        tryHandle(index + 1);
                        return;
                    }
                    
                    // If we have a base SKU, check if this product is related (has base SKU or variant)
                    if (baseSKULower && productData && productData.variants) {
                        var isRelatedProduct = productData.variants.some(function(variant) {
                            if (!variant.sku) return false;
                            var variantSKU = variant.sku.toLowerCase();
                            // Check if variant SKU matches base SKU or starts with base SKU + "."
                            return variantSKU === baseSKULower || variantSKU.indexOf(baseSKULower + '.') === 0;
                        });
                        
                        if (isRelatedProduct) {
                            callback(productData);
                        } else {
                            // Not a related product, try next handle
                            tryHandle(index + 1);
                        }
                    } else {
                        // No base SKU to check, or product has images, use it
                        callback(productData);
                    }
                }, function() {
                    // Product not found, try next handle
                    tryHandle(index + 1);
                });
            }
            
            tryHandle(0);
        }, function() {
            // Search failed
        });
    }
    
    // Function to fetch base product data from Shopify
    function fetchBaseProduct(baseSKU, variantSKU, callback) {
        var productType = getProductTypeFromSKU(baseSKU);
        var baseSKULower = baseSKU.toLowerCase();
        var completed = false;
        var MAX_TOTAL_ATTEMPTS = 8;
        var totalAttempts = 0;
        
        function resolve(productData) {
            if (completed) return;
            completed = true;
            callback(productData);
        }
        
        // Build handle attempts for base product (max 4 attempts)
        var attempts = [
            // Try with DNJ prefix and detected product type
            'dnj-engine-components-' + productType + '-' + baseSKULower,
            // Try with product type only
            productType + '-' + baseSKULower,
            // Try just the base SKU
            baseSKULower,
            // Try common piston-set pattern as fallback
            'piston-set-' + baseSKULower
        ];
        
        function tryFetch(index) {
            if (completed || totalAttempts >= MAX_TOTAL_ATTEMPTS) return;
            
            if (index >= attempts.length) {
                // Base product attempts exhausted, try SKU-based search with remaining attempts
                var remainingAttempts = MAX_TOTAL_ATTEMPTS - totalAttempts;
                trySKUSearch(remainingAttempts);
                return;
            }
            
            totalAttempts++;
            var handle = attempts[index];
            
            fetchProductByHandle(handle, function(productData) {
                // Check if this product has images
                if (hasProductImages(productData)) {
                    resolve(productData);
                } else {
                    // No images, try next pattern
                    tryFetch(index + 1);
                }
            }, function(error) {
                // Product not found, try next pattern
                tryFetch(index + 1);
            });
        }
        
        function trySKUSearch(maxAttempts) {
            if (completed) return;
            
            // Limit SKU search attempts to remaining attempts (max 4)
            var searchAttempts = Math.min(maxAttempts || 4, 4);
            
            // Try searching for base SKU
            fetchProductBySKUSearch(baseSKU, baseSKU, function(productData) {
                if (hasProductImages(productData)) {
                    resolve(productData);
                }
            }, searchAttempts);
        }
        
        tryFetch(0);
    }
    
    // Function to inject base product images into gallery
    function injectBaseProductImages(baseProductData) {
        if (!baseProductData.media || baseProductData.media.length === 0) {
            return;
        }
        
        // Log success message
        console.info('%c[Product Gallery Fallback] ✓ Successfully applied ' + baseProductData.media.length + ' image(s) from base product', 'color: #00aa00; font-weight: bold;');
        
        var viewer = document.querySelector('[data-gallery-viewer]');
        if (!viewer) {
            return;
        }
        
        // Clear existing content in viewer
        viewer.innerHTML = '';
        
        // Check if navigation exists - it might not for products without media
        var navigation = document.querySelector('[data-gallery-navigation]');
        var scroller = null;
        var galleryContainer = document.querySelector('[data-product-gallery]');
        
        // If navigation doesn't exist, create it
        if (!navigation && galleryContainer) {
            navigation = document.createElement('div');
            navigation.className = 'product-gallery--navigation';
            navigation.setAttribute('data-gallery-navigation', '');
            
            // Create scroll buttons
            var scrollLeftBtn = document.createElement('button');
            scrollLeftBtn.className = 'gallery-navigation--scroll-button scroll-left';
            scrollLeftBtn.setAttribute('aria-label', 'Scroll thumbnails left');
            scrollLeftBtn.setAttribute('data-gallery-scroll-button', '');
            scrollLeftBtn.innerHTML = '<svg aria-hidden="true" focusable="false" role="presentation" width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="icon-chevron-down-left" d="M7 6.75L12.5 1.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="square"></path><path class="icon-chevron-down-right" d="M7 6.75L1.5 1.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="square"></path></svg>';
            
            var scrollRightBtn = document.createElement('button');
            scrollRightBtn.className = 'gallery-navigation--scroll-button scroll-right';
            scrollRightBtn.setAttribute('aria-label', 'Scroll thumbnails right');
            scrollRightBtn.setAttribute('data-gallery-scroll-button', '');
            scrollRightBtn.innerHTML = '<svg aria-hidden="true" focusable="false" role="presentation" width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="icon-chevron-down-left" d="M7 6.75L12.5 1.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="square"></path><path class="icon-chevron-down-right" d="M7 6.75L1.5 1.25" stroke="currentColor" stroke-width="1.75" stroke-linecap="square"></path></svg>';
            
            // Create scroller
            scroller = document.createElement('div');
            scroller.className = 'gallery-navigation--scroller';
            scroller.setAttribute('data-gallery-scroller', '');
            
            navigation.appendChild(scrollLeftBtn);
            navigation.appendChild(scrollRightBtn);
            navigation.appendChild(scroller);
            
            // Append navigation to gallery container
            galleryContainer.appendChild(navigation);
        } else if (navigation) {
            scroller = navigation.querySelector('[data-gallery-scroller]');
            if (scroller) {
                scroller.innerHTML = '';
            }
        }
        
        // Get aspect ratio and image crop settings
        var aspectRatio = galleryContainer ? galleryContainer.getAttribute('data-product-gallery-aspect-ratio') : 'natural';
        var imageCrop = galleryContainer ? galleryContainer.hasAttribute('data-product-gallery-image-crop') : false;
        
        // Add images from base product
        baseProductData.media.forEach(function(media, index) {
            if (media.media_type === 'image') {
                var isSelected = index === 0;
                
                // Create figure element for main viewer
                var figure = document.createElement('figure');
                figure.className = 'product-gallery--media product-gallery--image';
                figure.setAttribute('tabindex', '-1');
                figure.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
                figure.setAttribute('data-gallery-figure', '');
                figure.setAttribute('data-gallery-index', index);
                figure.setAttribute('data-gallery-selected', isSelected ? 'true' : 'false');
                figure.setAttribute('data-media', media.id);
                figure.setAttribute('data-media-type', 'image');
                
                // Add zoom attributes
                var zoomSize = '2400x2400';
                var zoomUrl = media.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_' + zoomSize + '.$1');
                figure.setAttribute('data-zoom', zoomUrl);
                figure.setAttribute('data-image-height', '2400');
                figure.setAttribute('data-image-width', '2400');
                
                // Create image background div
                var bgDiv = document.createElement('div');
                bgDiv.className = 'product-gallery--image-background';
                
                // Create main image
                var img = document.createElement('img');
                img.className = 'product-gallery--loaded-image';
                img.src = media.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_x700.$1');
                img.alt = baseProductData.title;
                img.setAttribute('loading', index === 0 ? 'eager' : 'lazy');
                if (index === 0) {
                    img.setAttribute('fetchpriority', 'high');
                }
                img.setAttribute('data-rimg', 'loaded');
                
                bgDiv.appendChild(img);
                figure.appendChild(bgDiv);
                viewer.appendChild(figure);
                
                // Create thumbnail if navigation exists
                if (scroller) {
                    var thumbButton = document.createElement('button');
                    thumbButton.className = 'product-gallery--media-thumbnail product-gallery--image-thumbnail';
                    thumbButton.type = 'button';
                    thumbButton.setAttribute('tab-index', '0');
                    thumbButton.setAttribute('aria-label', baseProductData.title + ' thumbnail');
                    thumbButton.setAttribute('data-gallery-thumbnail', '');
                    thumbButton.setAttribute('data-gallery-index', index);
                    thumbButton.setAttribute('data-gallery-selected', isSelected ? 'true' : 'false');
                    thumbButton.setAttribute('data-media', media.id);
                    thumbButton.setAttribute('data-media-type', 'image');
                    
                    var thumbWrapper = document.createElement('span');
                    thumbWrapper.className = 'product-gallery--media-thumbnail-img-wrapper';
                    thumbWrapper.setAttribute('tabindex', '-1');
                    
                    var thumbImg = document.createElement('img');
                    var thumbSrc = media.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_75x75_crop_center.$1');
                    thumbImg.src = thumbSrc;
                    thumbImg.alt = '';
                    thumbImg.setAttribute('data-rimg', '');
                    
                    // Create srcset for thumbnail
                    var srcset = [
                        media.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_75x75_crop_center.$1') + ' 1x',
                        media.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_150x150_crop_center.$1') + ' 2x',
                        media.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_225x225_crop_center.$1') + ' 3x',
                        media.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_300x300_crop_center.$1') + ' 4x'
                    ].join(', ');
                    thumbImg.srcset = srcset;
                    thumbImg.className = 'product-gallery--media-thumbnail-img';
                    
                    thumbWrapper.appendChild(thumbImg);
                    thumbButton.appendChild(thumbWrapper);
                    scroller.appendChild(thumbButton);
                }
            }
        });
        
        // Add expand button if needed
        var expandButton = document.createElement('button');
        expandButton.className = 'product-gallery--expand';
        expandButton.setAttribute('data-gallery-expand', '');
        expandButton.setAttribute('aria-haspopup', 'true');
        
        expandButton.innerHTML = '<span class="click-text" tabindex="-1">' +
            '<svg class="icon-zoom" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="13" height="14" viewBox="0 0 13 14" fill="none">' +
            '<path fill-rule="evenodd" clip-rule="evenodd" d="M10.6499 5.36407C10.6499 6.71985 10.1504 7.95819 9.32715 8.90259L12.5762 12.6036L11.4233 13.6685L8.13379 9.92206C7.31836 10.433 6.35596 10.7281 5.3252 10.7281C2.38428 10.7281 0 8.3266 0 5.36407C0 2.40155 2.38428 0 5.3252 0C8.26611 0 10.6499 2.40155 10.6499 5.36407ZM9.29102 5.36407C9.29102 7.57068 7.51514 9.35956 5.32471 9.35956C3.13428 9.35956 1.3584 7.57068 1.3584 5.36407C1.3584 3.15741 3.13428 1.36859 5.32471 1.36859C7.51514 1.36859 9.29102 3.15741 9.29102 5.36407ZM4.9502 3.23438H5.80029V4.93439H7.5V5.78436H5.80029V7.48438H4.9502V5.78436H3.25V4.93439H4.9502V3.23438Z" transform="translate(0 0.015625)" fill="currentColor"></path></svg>' +
            'Click to expand</span>' +
            '<span class="tap-text" tabindex="-1">' +
            '<svg class="icon-tap" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="17" height="18" fill="none">' +
            '<g clip-path="url(#clip0)"><path d="M10.5041 5.68411c.3161-.69947.3733-1.48877.1212-2.24947C10.1368 1.96048 8.60378.947266 6.86197.947266c-1.7418 0-3.27478 1.013214-3.76334 2.487374-.25211.7607-.19492 1.55.12117 2.24947" stroke="currentColor" stroke-width="1.18421" stroke-linecap="round" stroke-linejoin="round"></path><path d="M1.29826 12.9496l.4482-.3869-.00078-.0009-.44742.3878zm2.1109-1.6885l.44817-.387-.00075-.0009-.44742.3879zm2.14476 2.484l-.44817.387c.16167.1872.42273.2542.6546.168.23187-.0863.38567-.3076.38567-.555h-.5921zm2.745-2.9235h-.59211c0 .327.26509.5921.59211.5921v-.5921zm-1.6868 7.3776l-4.86566-5.6365-.896406.7738L5.71571 18.973l.89641-.7738zm-4.86644-5.6374c-.26238-.3027-.22472-.749.09904-1.008l-.73973-.9248c-.84206.6736-.962533 1.8912-.254153 2.7084l.894843-.7756zm.09904-1.008c.33763-.2701.84237-.2218 1.11702.0951l.89484-.7757c-.69611-.8031-1.92341-.90664-2.75159-.2442l.73973.9248zm1.11627.0942l2.14476 2.4841.89634-.7739-2.14476-2.4841-.89634.7739zm3.18503 2.0971V5.10805H4.96181v8.63705h1.18421zm0-8.63705c0-.379.32677-.72639.7804-.72639V3.19745c-1.0624 0-1.96461.83323-1.96461 1.9106h1.18421zm.7804-.72639c.45362 0 .78039.34739.78039.72639h1.18421c0-1.07737-.90221-1.9106-1.9646-1.9106v1.18421zm.78039.72639v5.71355h1.18421V5.10805H7.70681zm.59211 6.30565H11.273v-1.1842H8.29892v1.1842zm2.97408 0c1.717 0 3.0679 1.3312 3.0679 2.9239h1.1842c0-2.2911-1.9264-4.1081-4.2521-4.1081v1.1842zm3.0679 2.9239v4.6098h1.1842v-4.6098h-1.1842z" fill="currentColor"></path></g><defs><clipPath id="clip0"><path fill="#fff" d="M0 0h16.6645v18H0z"></path></clipPath></defs></svg>' +
            'Tap to zoom</span>';
        
        viewer.appendChild(expandButton);
        
        // Mark viewer as having media
        viewer.classList.add('product-gallery--has-media');
        
        // Don't set inline display styles here - let updateArrowVisibility handle it
        // The function will properly show/hide based on desktop/mobile and position
        
        // Manually attach event handlers for gallery functionality
        setTimeout(function() {
            // Get all figures and thumbnails
            var figures = viewer.querySelectorAll('[data-gallery-figure]');
            var thumbnails = scroller ? scroller.querySelectorAll('[data-gallery-thumbnail]') : [];
            
            // Function to update navigation arrow visibility
            function updateArrowVisibility(index) {
                if (!navigation) return;
                
                var scrollButtons = navigation.querySelectorAll('[data-gallery-scroll-button]');
                if (scrollButtons.length !== 2) return;
                
                var leftButton = scrollButtons[0];
                var rightButton = scrollButtons[1];
                
                // Check if mobile (adjust breakpoint as needed)
                var isMobile = window.innerWidth < 720;
                
                if (!isMobile) {
                    // Desktop: hide both arrows by removing inline display and visible class
                    leftButton.style.display = 'none';
                    rightButton.style.display = 'none';
                    leftButton.classList.remove('visible');
                    rightButton.classList.remove('visible');
                } else {
                    // Mobile: show/hide based on position
                    leftButton.style.display = '';
                    rightButton.style.display = '';
                    
                    if (index === 0) {
                        // First image: hide left, show right
                        leftButton.classList.remove('visible');
                        rightButton.classList.add('visible');
                    } else if (index === figures.length - 1) {
                        // Last image: show left, hide right
                        leftButton.classList.add('visible');
                        rightButton.classList.remove('visible');
                    } else {
                        // Middle images: show both
                        leftButton.classList.add('visible');
                        rightButton.classList.add('visible');
                    }
                }
            }
            
            // Function to select a specific image
            function selectImage(index) {
                // Hide all figures
                figures.forEach(function(fig, i) {
                    if (i === index) {
                        fig.setAttribute('aria-hidden', 'false');
                        fig.setAttribute('data-gallery-selected', 'true');
                        fig.style.display = '';
                    } else {
                        fig.setAttribute('aria-hidden', 'true');
                        fig.setAttribute('data-gallery-selected', 'false');
                        fig.style.display = 'none';
                    }
                });
                
                // Update thumbnails
                thumbnails.forEach(function(thumb, i) {
                    if (i === index) {
                        thumb.setAttribute('data-gallery-selected', 'true');
                        thumb.classList.add('active');
                    } else {
                        thumb.setAttribute('data-gallery-selected', 'false');
                        thumb.classList.remove('active');
                    }
                });
                
                // Update arrow visibility based on current index
                updateArrowVisibility(index);
            }
            
            // Attach click handlers to thumbnails
            thumbnails.forEach(function(thumbnail, index) {
                thumbnail.addEventListener('click', function(e) {
                    e.preventDefault();
                    selectImage(index);
                });
            });
            
            // Attach click-to-zoom handler
            var zoomButton = viewer.querySelector('[data-gallery-expand]');
            if (zoomButton && galleryContainer.classList.contains('click-to-zoom-enabled')) {
                var handleZoomClick = function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Get the currently selected figure
                    var selectedFigure = viewer.querySelector('[data-gallery-selected="true"]');
                    if (!selectedFigure) return;
                    
                    var zoomUrl = selectedFigure.getAttribute('data-zoom');
                    var imageAlt = selectedFigure.querySelector('img').alt;
                    
                    // Create lightbox
                    var lightbox = document.createElement('div');
                    lightbox.className = 'product-gallery-lightbox';
                    lightbox.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.9); z-index: 9999; display: flex; align-items: center; justify-content: center; cursor: zoom-out;';
                    
                    // Create close button
                    var closeButton = document.createElement('button');
                    closeButton.className = 'product-gallery--lightbox-close';
                    closeButton.setAttribute('aria-label', 'Close zoom');
                    closeButton.style.cssText = 'position: absolute; top: 20px; right: 20px; width: 44px; height: 44px; background: rgba(255, 255, 255, 0.9); border: none; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 10000; transition: all 0.2s ease; box-shadow: 0 2px 8px rgba(0,0,0,0.3);';
                    closeButton.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
                    
                    // Close button hover effect
                    closeButton.addEventListener('mouseenter', function() {
                        this.style.background = 'rgba(255, 255, 255, 1)';
                        this.style.transform = 'scale(1.1)';
                    });
                    closeButton.addEventListener('mouseleave', function() {
                        this.style.background = 'rgba(255, 255, 255, 0.9)';
                        this.style.transform = 'scale(1)';
                    });
                    
                    var img = document.createElement('img');
                    img.src = zoomUrl;
                    img.alt = imageAlt;
                    img.style.cssText = 'max-width: 90%; max-height: 90%; object-fit: contain;';
                    
                    lightbox.appendChild(closeButton);
                    lightbox.appendChild(img);
                    document.body.appendChild(lightbox);
                    
                    // Function to close lightbox
                    var closeLightbox = function() {
                        if (document.body.contains(lightbox)) {
                            document.body.removeChild(lightbox);
                        }
                    };
                    
                    // Close on close button click
                    closeButton.addEventListener('click', function(e) {
                        e.stopPropagation();
                        closeLightbox();
                    });
                    
                    // Close on lightbox click (but not on image)
                    lightbox.addEventListener('click', function(e) {
                        if (e.target === lightbox) {
                            closeLightbox();
                        }
                    });
                    
                    // Close on escape
                    var escHandler = function(e) {
                        if (e.key === 'Escape') {
                            closeLightbox();
                            document.removeEventListener('keydown', escHandler);
                        }
                    };
                    document.addEventListener('keydown', escHandler);
                };
                
                zoomButton.addEventListener('click', handleZoomClick);
                viewer.addEventListener('click', function(e) {
                    // Only trigger if clicking the viewer itself, not thumbnails or buttons
                    if (e.target.closest('[data-gallery-figure]') || e.target === viewer) {
                        handleZoomClick(e);
                    }
                });
            }
            
            // Attach scroll button handlers for image navigation
            var currentImageIndex = 0;
            
            if (navigation) {
                var scrollButtons = navigation.querySelectorAll('[data-gallery-scroll-button]');
                if (scrollButtons.length === 2) {
                    // Left button: go to previous image
                    scrollButtons[0].addEventListener('click', function() {
                        if (currentImageIndex > 0) {
                            currentImageIndex--;
                            selectImage(currentImageIndex);
                            
                            // Also scroll thumbnails
                            if (scroller) {
                                var selectedThumb = thumbnails[currentImageIndex];
                                if (selectedThumb) {
                                    selectedThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                                }
                            }
                        }
                    });
                    
                    // Right button: go to next image
                    scrollButtons[1].addEventListener('click', function() {
                        if (currentImageIndex < figures.length - 1) {
                            currentImageIndex++;
                            selectImage(currentImageIndex);
                            
                            // Also scroll thumbnails
                            if (scroller) {
                                var selectedThumb = thumbnails[currentImageIndex];
                                if (selectedThumb) {
                                    selectedThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                                }
                            }
                        }
                    });
                }
            }
            
            // Update currentImageIndex when thumbnail is clicked
            thumbnails.forEach(function(thumbnail, index) {
                thumbnail.addEventListener('click', function() {
                    currentImageIndex = index;
                });
            });
            
            // Handle window resize to update arrow visibility
            window.addEventListener('resize', function() {
                updateArrowVisibility(currentImageIndex);
            });
            
            // Initialize by hiding all but first image
            selectImage(0);
        }, 100); // Small delay to ensure DOM is ready
    }
    
    // Main execution
    function init() {
        // Check if product has only placeholder
        if (!hasOnlyPlaceholder()) {
            return; // Product already has images
        }
        
        // Log informative message about expected 404 errors
        console.info('%c[Product Gallery Fallback] Searching for product images...', 'color: #0066cc; font-weight: bold;');
        console.info('%cNote: Network 404 errors below are expected behavior while searching for product images. They are not actual errors.', 'color: #666; font-style: italic;');
        
        // Get current product SKU from the page (try multiple methods)
        var currentSKU = null;
        
        // Method 1: Try .product-sku .sku element
        var skuElement = document.querySelector('.product-sku .sku');
        if (skuElement) {
            currentSKU = skuElement.textContent.trim().replace('#', '').replace(/\s+/g, '').trim();
        }
        
        // Method 2: Try product JSON if not found in DOM
        if (!currentSKU && typeof product !== 'undefined' && product.variants && product.variants.length > 0) {
            currentSKU = product.variants[0].sku;
        }
        
        // Method 3: Try meta tag
        if (!currentSKU) {
            var metaSKU = document.querySelector('meta[property="product:sku"]');
            if (metaSKU) {
                currentSKU = metaSKU.getAttribute('content');
            }
        }
        
        // Method 4: Try to extract from product handle (last resort)
        if (!currentSKU) {
            var productHandle = window.location.pathname.match(/\/products\/([^\/\?]+)/);
            if (productHandle && productHandle[1]) {
                // Try to fetch product by handle to get SKU
                fetchProductByHandle(productHandle[1], function(productData) {
                    if (productData && productData.variants && productData.variants.length > 0) {
                        var fetchedSKU = productData.variants[0].sku;
                        if (fetchedSKU) {
                            processSKU(fetchedSKU);
                        }
                    }
                }, function() {
                    // Failed to fetch, give up
                });
                return; // Will process async
            }
        }
        
        if (!currentSKU) {
            return;
        }
        
        processSKU(currentSKU);
    }
    
    // Helper function to process SKU (supports both variant and non-variant SKUs)
    function processSKU(currentSKU) {
        // Get base SKU (removes decimal part if variant, otherwise returns same SKU)
        var baseSKU = getBaseSKU(currentSKU);
        if (!baseSKU) {
            return;
        }
        
        // Check if this is a variant SKU (has decimal part)
        var isVariant = /\.\d+$/.test(currentSKU);
        
        if (isVariant) {
            // Variant SKU: use base SKU to find images
            fetchBaseProduct(baseSKU, currentSKU, injectBaseProductImages);
        } else {
            // Non-variant SKU with placeholder: try to find images using the same SKU
            // This handles cases like "WP4023" where the product itself doesn't have images
            // but a sibling product or product with similar handle might have images
            fetchBaseProduct(currentSKU, currentSKU, injectBaseProductImages);
        }
    }
    
    // Run on page load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();

